/*
 * A password field: the label wiring of `Field`, a visibility toggle that is a real button in the
 * tab order, and — for a password being chosen — the checklist of spec 01 §4.
 *
 * The checklist never says a password is good. It names the three rules the server actually applies
 * (`password.ts`), ticks the two the browser can see for itself, and leaves the third to the
 * sentence that says the server checks it: the verdict on a submitted password is the server's
 * (security.md §4.1), and a green "strong" beside a field is a promise the client cannot keep.
 */

import { useId, useState } from "react"
import { Button, Field } from "../../../ui/index.ts"
import { t } from "../../i18n/index.ts"
import { Icon } from "../../shell/icons.tsx"
import "../auth.css"
import {
	PASSWORD_MAX_LENGTH,
	PASSWORD_MIN_LENGTH,
	passwordRules,
	type PasswordRuleId,
} from "../password.ts"

/** One line of the checklist. The switch is exhaustive, so a fourth rule cannot ship untranslated. */
function ruleText(id: PasswordRuleId): string {
	switch (id) {
		case "length":
			return t("auth.password.rule.length", { min: PASSWORD_MIN_LENGTH })
		case "identifier":
			return t("auth.password.rule.identifier")
		case "leak":
			return t("auth.password.rule.leak")
	}
}

interface PasswordInputProps {
	/** Already translated. */
	label: string
	value: string
	onChange: (value: string) => void
	/** "current-password" for a password being proved, "new-password" for one being chosen. */
	autoComplete: "current-password" | "new-password"
	/** Already translated; one quiet line under a password that is being asked for. */
	hint?: string
	/** Already translated; its presence marks the field invalid. */
	error?: string
	/** Shows the checklist. Only a password being chosen has one. */
	advisory?: boolean
	/**
	 * The account's own handle and the local part of its address, where the screen knows them: a
	 * password may quote neither, and that rule can only be ticked where they are known.
	 */
	fragments?: readonly string[]
	disabled?: boolean
	autoFocus?: boolean
}

export function PasswordInput({
	label,
	value,
	onChange,
	autoComplete,
	hint,
	error,
	advisory = false,
	fragments = [],
	disabled = false,
	autoFocus = false,
}: PasswordInputProps) {
	const [shown, setShown] = useState(false)
	const rulesId = useId()

	return (
		<Field
			label={label}
			{...(hint === undefined ? {} : { hint })}
			{...(error === undefined ? {} : { error })}
		>
			{(control) => (
				<>
					{/* The toggle sits *inside* the control rather than beside it: as a flex sibling it
					    made the password field visibly shorter than the address field above it, which is
					    the first thing anybody sees on the sign-in screen (A-11 item 23). It is a mark and
					    not a word — an eye, crossed out while the password stands readable on screen — so
					    it takes the room a mark takes, and its accessible name is still a whole sentence. */}
					<span className="tb-auth__password">
						<input
							{...control}
							// The checklist is part of what describes this field, and `Field` owns only the
							// ids it minted itself, so the two lists are joined here.
							aria-describedby={
								advisory
									? [control["aria-describedby"], rulesId].filter(Boolean).join(" ")
									: control["aria-describedby"]
							}
							type={shown ? "text" : "password"}
							className="tb-field__control tb-auth__input"
							value={value}
							onChange={(e) => onChange(e.target.value)}
							autoComplete={autoComplete}
							maxLength={PASSWORD_MAX_LENGTH}
							disabled={disabled}
							autoFocus={autoFocus}
							required
						/>
						<Button
							className="tb-auth__reveal"
							variant="ghost"
							before={<Icon name={shown ? "eyeOff" : "eye"} size={16} />}
							aria-label={shown ? t("auth.password.hide") : t("auth.password.show")}
							aria-pressed={shown}
							disabled={disabled}
							onClick={() => setShown((current) => !current)}
						/>
					</span>
					{!advisory ? null : (
						<ul className="tb-password-rules" id={rulesId}>
							{passwordRules(value, fragments).map((rule) => (
								<li
									key={rule.id}
									className={`tb-password-rules__row${rule.met ? " tb-password-rules__row--met" : ""}`}
								>
									<span className="tb-password-rules__mark" aria-hidden="true">
										{rule.met ? <Icon name="check" size={14} /> : null}
									</span>
									{ruleText(rule.id)}
								</li>
							))}
						</ul>
					)}
				</>
			)}
		</Field>
	)
}
